import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { HrefComponent } from '@df/ui/common/href/href.component';
import {
    ButtonsOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    PaddingTopOptions,
    TextStyleOptions
} from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

export namespace NTextOverlay {
    export enum EType {
        text = 'text',
        button = 'button'
    }

    export interface IData {
        overlayText?: boolean;
        top_position: number;
        left_position: number;
        right_position?: number;
        lines: ILine[];
    }

    export interface ILine {
        type: EType;
        align: 'left' | 'center' | 'right';
        button_class: string;
        button_link: string;
        button_scale?: number;
        button_width?: number;
        text: string;
        text_style: string;
        text_size?: number;
        text_letter_spacing?: number;
        padding_top?: string;
        color: string;
        tag: string;
        shadow?: boolean;
    }

    export function getFields(withToggle = false): NICmsContentField.IField[] {
        const basicFileds = [
            <NICmsContentField.INumberField>{
                key: 'left_position',
                type: NICmsContentField.EFieldType.number,
                title: 'Shift from left in %',
                min: 0,
                max: 100,
                step: 1,
                default: 0
            },
            <NICmsContentField.INumberField>{
                key: 'right_position',
                type: NICmsContentField.EFieldType.number,
                title: 'Shift from right in %',
                min: 0,
                max: 100,
                step: 1,
                default: 0
            },
            <NICmsContentField.INumberField>{
                key: 'top_position',
                type: NICmsContentField.EFieldType.number,
                title: 'Shift from top in %',
                min: 0,
                max: 100,
                step: 1,
                default: 50
            },
            // text lines
            <NICmsContentField.IRepeaterField>{
                type: NICmsContentField.EFieldType.repeater,
                key: 'lines',
                title: 'Text lines',
                itemName: 'Line',
                itemNameField: 'text',
                defaultSize: 1,
                complex: true,
                fields: [
                    <NICmsContentField.IChoiceField>{
                        key: 'type',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Line type',
                        options: [
                            { key: NTextOverlay.EType.text, title: 'Text Line' },
                            { key: NTextOverlay.EType.button, title: 'Button' }
                        ],
                        default: 'text'
                    },
                    <NICmsContentField.IChoiceField>{
                        key: 'align',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Text alignment',
                        options: HorizontalAlignmentOptions,
                        default: 'center'
                    },
                    <NICmsContentField.IChoiceField>{
                        key: 'button_class',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Button style',
                        canShow: (data: NTextOverlay.ILine) => data?.type === NTextOverlay.EType.button,
                        options: ButtonsOptions,
                        default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined
                    },
                    <NICmsContentField.INumberField>{
                        key: 'button_scale',
                        type: NICmsContentField.EFieldType.number,
                        title: 'Button scale',
                        step: 0.1,
                        default: 1,
                        canShow: (data: NTextOverlay.ILine) => data?.type === NTextOverlay.EType.button
                    },
                    <NICmsContentField.INumberField>{
                        key: 'button_width',
                        type: NICmsContentField.EFieldType.number,
                        title: 'Button min width in px',
                        min: 0,
                        max: 500,
                        step: 1,
                        default: 0,
                        description: 'Value before scaling',
                        canShow: (data: NTextOverlay.ILine) => data?.type === NTextOverlay.EType.button
                    },
                    {
                        key: 'button_link',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Button link',
                        canShow: (data: NTextOverlay.ILine) => data?.type === NTextOverlay.EType.button
                    },
                    {
                        key: 'text',
                        type: NICmsContentField.EFieldType.textarea,
                        title: 'Copy',
                        default: 'Text'
                    },
                    <NICmsContentField.IChoiceField>{
                        key: 'text_style',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Text style',
                        options: TextStyleOptions,
                        canShow: (data: NTextOverlay.ILine) => data?.type !== NTextOverlay.EType.button
                    },
                    <NICmsContentField.INumberField>{
                        key: 'text_size',
                        type: NICmsContentField.EFieldType.number,
                        title: 'Text size in px',
                        min: 5,
                        max: 300,
                        step: 1,
                        default: 16,
                        canShow: (data: NTextOverlay.ILine) => data?.type !== NTextOverlay.EType.button
                    },
                    <NICmsContentField.INumberField>{
                        key: 'text_letter_spacing',
                        type: NICmsContentField.EFieldType.number,
                        title: 'Text letter spacing in px',
                        min: -5,
                        max: 100,
                        step: 1,
                        default: 0,
                        canShow: (data: NTextOverlay.ILine) => data?.type !== NTextOverlay.EType.button
                    },
                    <NICmsContentField.IColorPickerField>{
                        key: 'color',
                        type: NICmsContentField.EFieldType.colorPicker,
                        title: 'Text color',
                        canShow: (data: NTextOverlay.ILine) =>
                            data?.type !== NTextOverlay.EType.button || data?.button_class?.includes('button-inline')
                    },
                    <NICmsContentField.IChoiceField>{
                        key: 'tag',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Force tag',
                        description: 'For SEO reasons you may want to force particular HTML tag',
                        options: HtmlTagOptions,
                        canShow: (data: NTextOverlay.ILine) => data?.type !== NTextOverlay.EType.button
                    },
                    {
                        key: 'shadow',
                        type: NICmsContentField.EFieldType.toggle,
                        title: 'Add shadow',
                        description: 'Add shadow under the text'
                    },
                    <NICmsContentField.IChoiceField>{
                        key: 'padding_top',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Padding top',
                        options: PaddingTopOptions,
                        default: 'p-t-0'
                    }
                ]
            }
        ];
        return withToggle
            ? [
                  <NICmsContentField.IToggleField>{
                      key: 'overlayText',
                      type: NICmsContentField.EFieldType.toggle,
                      title: 'Show overlay text',
                      labels: { true: 'Overflow text', false: 'Basic funcionality' }
                  },
                  ...basicFileds.map(field => ({ ...field, canShow: (data: NTextOverlay.IData) => data?.overlayText }))
              ]
            : basicFileds;
    }
}

@Component({
    selector: 'text-overlay',
    templateUrl: './text-overlay.html',
    styleUrls: ['text-overlay.scss'],
    standalone: true,
    imports: [NgClass, ICmsClientModule, HrefComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextOverlayComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        watchDepth: 2
    })
    @Input({
        required: true
    })
    data!: NTextOverlay.IData;

    protected buttonShadow(buttonClass: string): string {
        if (buttonClass.match('button-inline')) {
            return '_underline-shadow _text-shadow';
        } else {
            return '_button-box-shadow';
        }
    }
}
